import React, { useEffect, useState } from "react";
import { collection, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import "./Orders.css";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [sortBy, setSortBy] = useState("Newest");
  const [filterDate, setFilterDate] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "orders"), (snapshot) => {
      const fetched = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setOrders(fetched);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleOrderClick = async (orderId) => {
    try {
      const docRef = doc(db, "orders", orderId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setSelectedOrder({ id: orderId, ...docSnap.data() });
      }
    } catch (err) {
      console.error("Error fetching order:", err);
    }
  };

  const filteredOrders = orders
    .filter((order) => {
      const search = searchTerm.toLowerCase();
      const matchSearch =
        order.id.toLowerCase().includes(search) ||
        order.customer?.name?.toLowerCase().includes(search) ||
        order.customer?.phone?.toLowerCase().includes(search);

      const matchStatus =
        filterStatus === "All" || order.status === filterStatus;

      const matchDate = !filterDate
        ? true
        : new Date(order.createdAt?.seconds * 1000)
            .toISOString()
            .split("T")[0] === filterDate;

      return matchSearch && matchStatus && matchDate;
    })
    .sort((a, b) => {
      if (sortBy === "Newest") {
        return b.createdAt?.seconds - a.createdAt?.seconds;
      } else if (sortBy === "Oldest") {
        return a.createdAt?.seconds - b.createdAt?.seconds;
      } else if (sortBy === "HighTotal") {
        return (b.totalAmount || 0) - (a.totalAmount || 0);
      } else if (sortBy === "LowTotal") {
        return (a.totalAmount || 0) - (b.totalAmount || 0);
      } else {
        return 0;
      }
    });

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "Arial, sans-serif" }}>
      {/* Left: Orders list */}
      <div style={{ flex: 1, padding: "20px", background: "#f9f9f9" }}>
        <div
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            padding: "16px 20px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            zIndex: 10,
          }}
        >
          <h2 style={{ marginBottom: "10px" }}>🧾 All Orders</h2>
          <input
            type="text"
            placeholder="Search by name, phone or order ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "10px",
              width: "100%",
              borderRadius: "8px",
              border: "1px solid #ccc",
              marginBottom: "10px",
            }}
          />

          {/* Filters */}
          <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ padding: "8px", borderRadius: "6px" }}
            >
              <option value="All">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Delivered">Delivered</option>
              <option value="Cancelled">Cancelled</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ padding: "8px", borderRadius: "6px" }}
            >
              <option value="Newest">Newest First</option>
              <option value="Oldest">Oldest First</option>
              <option value="HighTotal">Total: High to Low</option>
              <option value="LowTotal">Total: Low to High</option>
            </select>

            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              style={{ padding: "8px", borderRadius: "6px" }}
            />
          </div>
        </div>

        <div style={{ marginTop: "20px" }}>
          {loading ? (
            <p>Loading orders...</p>
          ) : filteredOrders.length === 0 ? (
            <p>No orders found.</p>
          ) : (
            filteredOrders.map((order) => (
              <div
                key={order.id}
                className="order-box"
                onClick={() => handleOrderClick(order.id)}
              >
                <p><strong>Order ID:</strong> {order.id}</p>
                <p><strong>Customer:</strong> {order.customer?.name || "Unknown"}</p>
                <p><strong>Phone:</strong> {order.customer?.phone || "-"}</p>
                <p><strong>Status:</strong> {order.status || "Pending"}</p>
                <p><strong>Date:</strong> {new Date(order.createdAt?.seconds * 1000).toLocaleDateString()}</p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right: Side Panel */}
      {selectedOrder && (
        <div style={{
          width: "400px",
          backgroundColor: "#fff",
          borderLeft: "1px solid #ccc",
          padding: "20px",
          overflowY: "auto",
          position: "relative"
        }}>
          <button
            onClick={() => setSelectedOrder(null)}
            style={{
              position: "absolute",
              top: "10px",
              right: "15px",
              fontSize: "22px",
              border: "none",
              background: "transparent",
              cursor: "pointer"
            }}
          >
            ✖
          </button>

          <h2>Order Details</h2>
          <p><strong>Order ID:</strong> {selectedOrder.id}</p>
          <p><strong>Customer:</strong> {selectedOrder.customer?.name}</p>
          <p><strong>Phone:</strong> {selectedOrder.customer?.phone}</p>
          <p><strong>Email:</strong> {selectedOrder.customer?.email}</p>

          <hr />

          <h3>Items:</h3>
          {selectedOrder.items?.map((item, index) => (
            <div key={index}>
              <p>{item.name} - Qty: {item.quantity} - ₹{item.price}</p>
            </div>
          ))}

          <hr />
          <p><strong>Total:</strong> ₹{selectedOrder.totalAmount}</p>
          <p><strong>Discount:</strong> ₹{selectedOrder.appliedDiscounts || 0}</p>
          <p><strong>Taxes:</strong> ₹{selectedOrder.taxes || 0}</p>
          <p><strong>Payment:</strong> {selectedOrder.paymentStatus} via {selectedOrder.paymentMethod}</p>

          <hr />
          <p><strong>Address:</strong> {selectedOrder.deliveryInfo?.address}</p>
          <p><strong>ETA:</strong> {selectedOrder.deliveryInfo?.eta}</p>
          <p><strong>Status:</strong> {selectedOrder.deliveryInfo?.status}</p>
        </div>
      )}
    </div>
  );
};

export default Orders;
